/**
 * Development Logger Utility
 *
 * Conditional logging that only runs in development mode.
 * In production builds, these calls are stripped out automatically.
 *
 * @example
 * import { logger } from '@/shared/utils/logger'
 * logger.debug('Map loaded', { districts: 32 })
 * logger.info('User action', { action: 'filter' })
 * logger.warn('Slow query', { duration: 1200 })
 * logger.error('API failed', error)
 */

const isDev = import.meta.env.DEV

class Logger {
  /**
   * Debug-level logging (verbose, development only)
   */
  debug(message: string, ...args: unknown[]): void {
    if (isDev) {
      console.log(`🔍 ${message}`, ...args)
    }
  }

  /**
   * Info-level logging (important events, development only)
   */
  info(message: string, ...args: unknown[]): void {
    if (isDev) {
      console.log(`ℹ️ ${message}`, ...args)
    }
  }

  /**
   * Warning-level logging (shown in production)
   */
  warn(message: string, ...args: unknown[]): void {
    console.warn(`⚠️ ${message}`, ...args)
  }

  /**
   * Error-level logging (shown in production)
   */
  error(message: string, ...args: unknown[]): void {
    console.error(`❌ ${message}`, ...args)

    // TODO: Send to error tracking service (Sentry) in production
    // if (!isDev && window.Sentry) {
    //   window.Sentry.captureException(new Error(message), {
    //     extra: { args }
    //   })
    // }
  }
}

export const logger = new Logger()

/**
 * For quick migration from console.log
 * @deprecated Use logger.debug() instead
 */
export const log = logger.debug.bind(logger)
